import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, MapPin, Clock, Package, CheckCircle } from 'lucide-react';

export default function ShippingPage() {
  return (
    <div className="pt-28 pb-16" data-testid="shipping-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Delivery Information</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Shipping Policy
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            We're proud to offer free shipping across Cyprus on all orders.
          </p>
        </div>

        {/* Free Shipping Banner */}
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 rounded-sm mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Truck className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-[#354F52]">Free Shipping in Cyprus!</h2>
          </div>
          <p className="text-[#52796F]">All orders ship free within Cyprus via ACS Couriers</p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <Truck className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">Free Delivery</h3>
            <p className="text-sm text-[#52796F]">No minimum order required</p>
          </div>
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <Clock className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">2-4 Business Days</h3>
            <p className="text-sm text-[#52796F]">Delivery time within Cyprus</p>
          </div>
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <Package className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">Careful Packaging</h3>
            <p className="text-sm text-[#52796F]">Beautifully gift-wrapped</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-10">
          {/* Shipping Partner */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">1</span>
              Our Shipping Partner
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                We partner with <strong className="text-[#354F52]">ACS Couriers</strong> to deliver 
                your handcrafted bracelets safely across Cyprus. ACS is known for reliable and 
                careful handling of packages.
              </p>
              <div className="bg-[#F5F5F0] p-4 rounded-sm">
                <p className="font-medium text-[#354F52] mb-2">Our Location</p>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span>Online &amp; markets around Paphos, Cyprus</span>
                </div>
              </div>
            </div>
          </section>

          {/* Delivery Times */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">2</span>
              Delivery Times
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <div className="bg-[#F5F5F0] p-4 rounded-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#354F52]">
                      <th className="pb-3 font-medium">Order Type</th>
                      <th className="pb-3 font-medium">Processing</th>
                      <th className="pb-3 font-medium">Delivery</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#52796F]">
                    <tr className="border-t border-[#CAD2C5]/50">
                      <td className="py-3">Standard Collection</td>
                      <td className="py-3">1-2 days</td>
                      <td className="py-3">2-4 days</td>
                    </tr>
                    <tr className="border-t border-[#CAD2C5]/50">
                      <td className="py-3">Custom Orders</td>
                      <td className="py-3">3-5 days</td>
                      <td className="py-3">2-4 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm">
                <strong className="text-[#354F52]">Note:</strong> Processing time is the time needed to 
                handcraft your bracelet. Custom orders take longer as each piece is made to order.
              </p>
            </div>
          </section>

          {/* Order Tracking */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">3</span>
              Order Tracking
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                Once your order ships, you'll receive an email with your <strong className="text-[#354F52]">ACS tracking number</strong>. 
                You can track your package at{' '}
                <a 
                  href="https://www.acscourier.net" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:underline"
                >
                  www.acscourier.net
                </a>
              </p>
            </div>
          </section>

          {/* What's Included */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">4</span>
              What's Included
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>Every order arrives beautifully packaged and includes:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span>Your handcrafted Morse code bracelet</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span>Signature gift box</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span>Meaning card with Morse code translation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span>Care instructions</span>
                </li>
              </ul>
            </div>
          </section>

          {/* International Shipping */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">5</span>
              International Shipping
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                We currently ship within <strong className="text-[#354F52]">Cyprus only</strong>. 
                International shipping is coming soon!
              </p>
              <p>
                Interested in international delivery? <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact us</Link> and 
                we'll notify you when it becomes available.
              </p>
            </div>
          </section>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 p-8 bg-[#354F52] rounded-sm text-center text-[#FAF9F6]">
          <Truck className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl mb-3">Questions About Shipping?</h3>
          <p className="text-[#CAD2C5] mb-6 max-w-md mx-auto">
            We're happy to help with any delivery questions.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-[#D4AF37] hover:bg-[#B8972E] text-[#354F52] px-8 py-3 rounded-full font-medium uppercase tracking-widest text-xs transition-colors"
            data-testid="contact-btn"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
